import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RootState, useAppDispatch, useAppSelector } from '../../../state/state';
import { closeModal } from '../../../state/ModalSlice/ModalSlice';
import { TextFieldComponent, DropdownComponent, RadioComponent, CheckboxComponent } from '../customFieldsFunctions';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { toast } from 'react-toastify';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

type FieldType = 'textfield' | 'dropdown' | 'radio' | 'checkbox';

const fieldTypes: { type: FieldType; label: string; icon: string; description: string }[] = [
  { 
    type: 'textfield', 
    label: 'Text Field', 
    icon: '📝', 
    description: 'Single line text input for names, emails, etc.' 
  },
  { 
    type: 'dropdown', 
    label: 'Dropdown', 
    icon: '📋', 
    description: 'Select one option from a predefined list' 
  },
  { 
    type: 'radio', 
    label: 'Yes/No Choice', 
    icon: '⚪', 
    description: 'Binary choice with two options (Yes/No, True/False)' 
  },
  { 
    type: 'checkbox', 
    label: 'Checkbox', 
    icon: '☑️', 
    description: 'Single checkbox for agreements or confirmations' 
  },
];

const AddCustomerKYCFieldModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state: RootState) => state.ModalSlice.AddCustomerKYCFieldModalOpen);
  const serviceProviderId = useAppSelector(selectEffectiveUID); 
  
  const [selectedType, setSelectedType] = useState<FieldType | null>(null);
  const [fieldData, setFieldData] = useState<any>({
    fieldDescription: "",
    isRequired: false,
    options: []
  });

  const handleClose = () => {
    dispatch(closeModal("AddCustomerKYCFieldModalOpen"));
    setSelectedType(null);
    setFieldData({
      fieldDescription: "",
      isRequired: false,
      options: []
    });
  };

  const handleTypeSelect = (type: FieldType) => {
    setSelectedType(type);
  };

  const handleFieldChange = (newData: { fieldDescription: string; isRequired: boolean; options?: string[] }) => {
    setFieldData(newData);
  };

  const handleSubmit = async (newField: { 
    fieldDescription: string; 
    isRequired: boolean; 
    type: string; 
    options?: string[] 
  }) => {
    try {
      const serviceProviderRef = doc(db, 'ServiceProviders', serviceProviderId);
      
      // Generate unique ID for the field
      const fieldId = Date.now();
      const fieldToAdd = {
        ...newField,
        id: fieldId
      };

      // Add to the customerKYCFields array
      await updateDoc(serviceProviderRef, {
        customerKYCFields: arrayUnion(fieldToAdd)
      });

      toast.success('KYC field added successfully');
      handleClose();
    } catch (error) {
      console.error('Error adding custom field:', error);
      toast.error('Error adding field');
    }
  };

  const renderFieldComponent = () => {
    if (!selectedType) return null;

    const props = {
      fieldDescription: fieldData.fieldDescription,
      isRequired: fieldData.isRequired,
      existingOptions: fieldData.options,
      onFieldChange: handleFieldChange,
      onSubmit: handleSubmit,
      optional: true
    };

    switch (selectedType) {
      case 'textfield':
        return <TextFieldComponent {...props} />;
      case 'dropdown':
        return <DropdownComponent {...props} />;
      case 'radio':
        return <RadioComponent {...props} />;
      case 'checkbox':
        return <CheckboxComponent {...props} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      sx={{
        padding: 0,
        '& .MuiPaper-root': {
          borderRadius: '10px',
        },
      }}
      maxWidth="md"
      open={isOpen}
      PaperProps={{
        component: 'div',
        className: 'relative',
      }}
    >
      <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className="bg-white">
        <div className="w-[550px]">
          <div className="header pb-[15px] space-y-2">
            {selectedType && (
              <button
                onClick={() => setSelectedType(null)}
                className="mb-2 flex items-center gap-1.5 text-gray-600 hover:text-gray-800 transition-colors duration-200 group"
              >
                <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-200" 
                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back</span>
              </button>
            )}
            {!selectedType && <h2 className="text-2xl font-semibold text-gray-800 transition-all duration-300">
              {selectedType ? 'Add KYC Field' : 'Add Customer KYC Field'}
            </h2>}
            {!selectedType && <p className="text-gray-500 font-medium">
              Create a custom field to collect customer information during registration.
            </p>}
          </div>

          <div className="absolute right-4 top-4">
            <IconButton 
              onClick={handleClose}
              className="hover:rotate-90 transition-transform duration-300"
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                }
              }}
            >
              <CloseIcon className="text-gray-500 hover:text-gray-700" />
            </IconButton>
          </div>

          {!selectedType ? (
            <div className="grid grid-cols-2 gap-4">
              {fieldTypes.map((fieldType) => (
                <button
                  key={fieldType.type}
                  onClick={() => handleTypeSelect(fieldType.type)}
                  className="group flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="text-3xl group-hover:scale-110 transition-transform duration-300">
                    {fieldType.icon}
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 transition-colors duration-300">
                      {fieldType.label}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {fieldType.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <Box className="min-h-[200px]">
              {renderFieldComponent()}
            </Box>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomerKYCFieldModal;
